import React, { useEffect } from 'react'
import { useTranslation } from 'next-i18next'
import { UseFormReturn } from 'react-hook-form'
import { object, boolean } from 'yup'
import useOrder from 'modules/order/state/order/use-order'
import CheckboxField from 'modules/ui/molecules/FormComponents/CheckboxField'
import { Tooltip } from 'modules/ui/atoms/Tooltip'
import { UiIcon } from 'modules/ui/atoms/Icon'
import styled from '@emotion/styled'
import DonationOptionFormWrapper from './donation-option-form-wrapper'

const TooltipContainer = styled.span`
  margin-left: 8px;
  display: inline-flex;
  align-items: center;
`

const CheckboxWithTooltip = styled.div`
  display: flex;
  align-items: center;
`

export type AnonymousNameFormData = {
  isNameAnonymous: boolean
}

const AnonymousNameOptionFields = ({ form }: { form: UseFormReturn<AnonymousNameFormData> }) => {
  const { t } = useTranslation()
  const order = useOrder()

  const {
    watch,
    formState: { errors },
  } = form

  const isNameAnonymous = watch('isNameAnonymous')

  useEffect(() => {
    order.contact.updateField('isNameAnonymous', isNameAnonymous)
  }, [isNameAnonymous, order.contact])

  return (
    <CheckboxWithTooltip>
      <CheckboxField
        id="isNameAnonymous"
        dataId="donor-privacy-anonymous-name-checkbox"
        error={
          errors.isNameAnonymous
            ? { message: errors.isNameAnonymous.message || 'Invalid value' }
            : undefined
        }
      >
        {t('OrderAnonymousFields.hideName.alternateLabel', 'Hide my name')}
      </CheckboxField>
      <TooltipContainer>
        <Tooltip
          placement="top"
          text={t(
            'OrderAnonymousFields.hideName.alternateTooltip',
            'Your name will be "anonymous" on our public donation feed',
          )}
        >
          <UiIcon name="info" color="#78726E" ariaHidden />
        </Tooltip>
      </TooltipContainer>
    </CheckboxWithTooltip>
  )
}

const AnonymousNameOption = () => {
  const validationSchema = object({
    isNameAnonymous: boolean().required(),
  })

  const useAnonymousNameValues = (): AnonymousNameFormData => {
    const order = useOrder()

    return {
      isNameAnonymous: order.contact.value.isNameAnonymous,
    }
  }

  return (
    <DonationOptionFormWrapper<AnonymousNameFormData>
      formName="anonymousName"
      errorMessage="Please review your anonymous name option"
      validationSchema={validationSchema}
      useInitialValues={useAnonymousNameValues}
    >
      {(form) => <AnonymousNameOptionFields form={form} />}
    </DonationOptionFormWrapper>
  )
}

export default AnonymousNameOption
